/**
 * avatarScheduler — pure A/V lip-sync decision logic (phase 3.2, Option B+).
 *
 * No React, no WebSocket, no DOM: just the timing math so it is unit-testable
 * with esbuild+node. Two timelines (reply audio played in the browser, and the
 * MuseTalk video PTS) are reconciled here:
 *   - `pickFrame`      — which buffered video frame to present against the audio clock.
 *   - `computePlayedMs`/`scheduleChunk` — the gap-corrected browser CONTENT clock.
 *   - `AudioGate`      — the video-gated audio-start state machine.
 *   - `parseFrameHeader` — the 12-byte per-frame WS header (must match wsjpeg.py).
 *
 * See phases/3.2-avatar-avsync/DESIGN.md §2, §4, §5.
 */

import { type AvsyncPersistent, newAvsyncPersistent } from './sync-telemetry';
import type { PlaybackClock } from '../voice/controller';

// ── Tuning constants (single source; DESIGN §10 item 13) ──
export const LEAD_MS = 0; // present frames at exactly the played audio position
export const STALE_MS = 200; // drop backlog more than this behind the audio target
export const MAX_BUFFER_MS = 1600; // jitter-buffer depth cap (~40 frames @25fps)
export const GATE_MAX_MS = 1500; // cap > measured TTFF (~1025ms): the gate normally releases audio ON the real first frame (tight onset lip-sync); still a safety cap if no frame ever arrives. Kept < MAX_BUFFER_MS so the buffer can't prune frames the gate is still waiting on.
export const HOLD_MAX_MS = 2500; // frozen-clock escape (phase 3.8, W5.4): if pickFrame answers 'hold' continuously for longer than this (audio clock stuck non-null on a mid-reply underrun), fall back to the resting face instead of freezing on the last lip frame forever.

// ── Idle seam-continuity constants (SPEC 4.9.1) ──
// Speaking frames are PTS-buffered and presented behind arrival (BurstPacer fills
// the jitter buffer ahead of audio), while idle frames used to collapse to a single
// latest-wins bitmap. At every displayed speech→idle handoff the canvas teleported
// to the NEWEST idle pose, skipping ~10–40 plate frames and overwriting the 4.9
// server ramp. These knobs drive the SeamScheduler that instead plays the buffered
// idle run sequentially and catches up to live.
export const SEAM_CATCHUP_RATE = 1.3; // playback speed-up while draining the seam backlog; gentle enough to be invisible, drains a worst-case MAX_BUFFER_MS (1600ms) backlog in ~5s.
export const SEAM_MAX_MS = 3000; // idle-queue cap (drop oldest); ~75 frames ≈ 7.5MB of JPEG bytes; ≥2× worst-case presentation lag (MAX_BUFFER_MS).
export const IDLE_FRAME_MS = 40; // nominal idle cadence (25fps).
export const SEAM_FRAME_MS = IDLE_FRAME_MS / SEAM_CATCHUP_RATE; // catch-up inter-frame interval (~30.8ms) — single source, derived so the rate is the only knob.
export const SEAM_MAX_FRAMES = Math.floor(SEAM_MAX_MS / IDLE_FRAME_MS); // idle-queue depth cap in frames (75).

// ── Wire format (DESIGN §3.1; mirrors avatar service wsjpeg.py, little-endian) ──
export const FRAME_MAGIC = 0xa511; // JPEG SOI 0xFFD8 reads 0xD8FF LE → never collides
export const FRAME_HEADER_BYTES = 12;
// v2 = "solo bocca" (avatar/livetalking/plate_crop.py): + idx, x, y, reserved (u16 each)
export const FRAME_HEADER_V2_BYTES = 20;

const FLAG_SPEAKING = 0x1;
const FLAG_IDLE = 0x2;

// ── Types ──

/** A buffered video frame's schedulable identity (bitmap kept by the component). */
export interface FrameRef {
  pts: number;
  turn: number;
}

export interface PickOpts {
  leadMs: number;
  staleMs: number;
}

/**
 * Hold-timeout tracking (W5.4). The caller records the wall time of the last
 * non-`hold` decision (`lastActiveMs`) and passes the current `nowMs`; when a
 * `hold` has persisted longer than HOLD_MAX_MS, pickFrame escapes to `idle`.
 */
export interface HoldState {
  nowMs: number;
  lastActiveMs: number;
}

/** Video scheduler decision for one rAF tick. */
export type PickResult =
  | { kind: 'idle' } // resting face (clock null, or all due frames consumed)
  | { kind: 'hold' } // leave canvas as-is (frames buffered but not yet due)
  | { kind: 'draw'; pts: number; dropBelowPts: number };

export interface FrameHeader {
  version: number;
  turn: number;
  pts: number;
  speaking: boolean;
  idle: boolean;
  /** v2 only: plate frame to draw, and where the mouth crop (if any) goes. */
  idx?: number;
  x?: number;
  y?: number;
}

/** Running content-clock state, mirrored by refs in the audio hook (DESIGN §5.1). */
export interface ClockState {
  utteranceStartSec: number | null;
  gapMs: number;
  scheduledContentMs: number;
  nextPlaybackSec: number;
}

export interface ScheduleResult {
  state: ClockState;
  startSec: number;
}

// ── Video scheduler ──

/**
 * Choose the newest buffered frame whose PTS is at or before the played-audio
 * target for the current turn (DESIGN §4). Frames from other turns are ignored.
 * Returns the frame to draw plus the eviction floor, or a hold/idle decision.
 */
export function pickFrame(
  entries: FrameRef[],
  clock: Pick<PlaybackClock, 'turn' | 'playedMs'> | null, // the frame needs where the audio is, not how long it is
  opts: PickOpts,
  hold?: HoldState,
): PickResult {
  if (clock == null) return { kind: 'idle' };
  const target = clock.playedMs - opts.leadMs;
  let hasTurnEntry = false;
  let pick = Number.NEGATIVE_INFINITY;
  for (const e of entries) {
    if (e.turn !== clock.turn) continue;
    hasTurnEntry = true;
    if (e.pts <= target && e.pts > pick) pick = e.pts;
  }
  if (pick !== Number.NEGATIVE_INFINITY) {
    // Evict frames both surpassed (< pick) and stale (< target-STALE): DESIGN §4.
    const dropBelowPts = Math.min(pick, target - opts.staleMs);
    return { kind: 'draw', pts: pick, dropBelowPts };
  }
  // Nothing due: resting face once frames are consumed, else hold (never regress).
  if (!hasTurnEntry) return { kind: 'idle' };
  // W5.4: a hold that never resolves (frozen non-null clock mid-reply) would freeze
  // the mouth on its last lip frame forever — escape to the resting face after HOLD_MAX_MS.
  if (hold && hold.nowMs - hold.lastActiveMs > HOLD_MAX_MS) return { kind: 'idle' };
  return { kind: 'hold' };
}

/** Jitter-buffer floor: evict frames further than maxBufferMs behind the newest. */
export function bufferDropThreshold(newestPts: number, maxBufferMs: number): number {
  return newestPts - maxBufferMs;
}

/**
 * W4.2 epoch-overlap guard: a new `assistant_audio_start` must stop the previous
 * turn's still-scheduled audio sources so two replies never sound at once (RC6).
 * True when the incoming epoch differs from the armed one AND sources are still
 * queued (they can only belong to the prior turn — the new turn isn't scheduled yet).
 */
export function shouldStopPreviousEpoch(
  prevEpoch: number | null,
  newEpoch: number | null,
  scheduledCount: number,
): boolean {
  return prevEpoch !== newEpoch && scheduledCount > 0;
}

// ── Idle seam scheduler (SPEC 4.9.1) ──

/** Mode machine for idle presentation. Steady state is `latest` (latest-wins, the
 *  pre-4.9.1 look); a displayed speech→idle transition switches to `seam`, which
 *  plays the buffered idle run in arrival order until it catches up to live. */
export type SeamMode = 'latest' | 'seam';

/** What the component should do with the idle slot this present tick. Items are
 *  opaque (the component stores JPEG-byte objects and decodes on present). */
export type SeamAction<T> =
  | { kind: 'present'; item: T } // decode + draw this idle frame
  | { kind: 'hold' }; // nothing new is due — leave the canvas as-is

/**
 * Pure idle-frame scheduler (SPEC 4.9.1). Owns the arrival-order idle store and the
 * latest⇄seam mode machine; no DOM, no decode, no timers — the component feeds it
 * `nowMs` and per-tick draw/idle events and acts on the returned {@link SeamAction}.
 *
 * DESIGN: the store is ALWAYS the arrival-order queue (R1). During speech the queue
 * accumulates the frames the server sent ahead (the 4.9 ramp-out + trailing idle);
 * at the displayed speech→idle boundary we drain it from the FRONT at
 * SEAM_CATCHUP_RATE (R2/R3) so the plate pose stays continuous and the ramp is
 * actually shown, then revert to latest-wins once caught up to the newest arrival.
 *
 * Flush semantics (R4) are keyed on ARRIVAL, not display: {@link onSpeakingFrame} is
 * called when a speaking frame arrives from the server. Because frames arrive in
 * monotonic plate order, every idle frame already queued is BEHIND that speaking
 * frame (lower plate index) and would rewind the plate if shown at the eventual seam,
 * so the queue is dropped. Idle frames that arrive AFTER the last speaking frame (the
 * ramp-out + trailing idle at reply end) are ahead and survive to drain. This covers
 * both mid-reply clause gaps (next clause's speaking arrival flushes the gap idle) and
 * end-of-reply (no speaking arrival follows ⇒ ramp preserved) — the display-time
 * {@link onSpeakingDraw} cannot distinguish them because BurstPacer delivers frames
 * ahead of display. Barge-in {@link reset}s everything (R5).
 */
export class SeamScheduler<T> {
  private queue: T[] = [];
  private _mode: SeamMode = 'latest';
  private speaking = false; // the last displayed frame was a speaking draw
  private nextSeamAtMs = 0; // wall time the next seam frame may present (catch-up pacing)

  get mode(): SeamMode {
    return this._mode;
  }
  /** Current idle-queue depth (buffered idle frames not yet presented). */
  get depth(): number {
    return this.queue.length;
  }

  /** Buffer one idle frame in arrival order; drop the oldest past the SEAM_MAX_MS cap. */
  enqueue(item: T): void {
    pushRing(this.queue, item, SEAM_MAX_FRAMES);
  }

  /**
   * A speaking frame ARRIVED from the server (R4). In monotonic plate order it sits
   * ahead of every idle frame already queued, so those idle frames are now behind the
   * advancing plate and must not be shown at the eventual seam (they would rewind it) —
   * drop the backlog. Ramp-out / trailing idle arriving after the last speaking frame
   * are not flushed and survive to drain the seam.
   */
  onSpeakingFrame(): void {
    this.queue = [];
  }

  /**
   * A speaking frame is DISPLAYED this tick — record that the last displayed frame is
   * speaking so the next 'idle' pick is detected as a speech→idle boundary that engages
   * the seam ({@link onIdleTick}). Idempotent; does NOT flush (the arrival-keyed
   * {@link onSpeakingFrame} owns flushing) so idle frames buffered ahead during the run
   * survive to the boundary drain.
   */
  onSpeakingDraw(): void {
    this.speaking = true;
  }

  /**
   * Decide the idle slot for one present tick. On the displayed speech→idle
   * transition (any draw→idle, including the W5.4 hold-escape) engage seam playback
   * from the first buffered idle frame; otherwise drain (seam) or show newest (latest).
   */
  onIdleTick(nowMs: number): SeamAction<T> {
    if (this.speaking) {
      this.speaking = false;
      this._mode = 'seam';
      this.nextSeamAtMs = nowMs; // present the first seam frame immediately
    }
    return this._mode === 'seam' ? this.drainSeam(nowMs) : this.showLatest();
  }

  /** Barge-in / unmount: drop the queue and reset the mode machine (R5). */
  reset(): void {
    this.queue = [];
    this._mode = 'latest';
    this.speaking = false;
    this.nextSeamAtMs = 0;
  }

  /** Seam mode: pop the oldest buffered frame once per catch-up interval; revert to
   *  latest-wins once the backlog has drained to the newest arrival (R3). */
  private drainSeam(nowMs: number): SeamAction<T> {
    if (this.queue.length === 0) {
      this._mode = 'latest'; // empty at engage (R2) or fully drained → latest-wins
      return { kind: 'hold' };
    }
    if (nowMs < this.nextSeamAtMs) return { kind: 'hold' };
    const item = this.queue.shift() as T;
    this.nextSeamAtMs = nowMs + SEAM_FRAME_MS; // relative pacing: never bursts past 1.3×
    if (this.queue.length === 0) this._mode = 'latest'; // caught up to live
    return { kind: 'present', item };
  }

  /** Latest-wins steady state: present the newest arrival (skipping any older frames)
   *  and consume the queue; the queue-empty guard suppresses re-presenting a stale
   *  frame (idle arrivals are distinct byte objects, so a value-dedupe would be dead). */
  private showLatest(): SeamAction<T> {
    if (this.queue.length === 0) return { kind: 'hold' };
    const newest = this.queue[this.queue.length - 1]!;
    this.queue = [];
    return { kind: 'present', item: newest };
  }
}

// ── Content clock (DESIGN §5.1) ──

/**
 * Played reply-content ms = wall-since-utterance minus underrun gaps, clamped to
 * what has actually been scheduled (freezes the clock during an in-progress gap).
 */
export function computePlayedMs(wallMs: number, gapMs: number, scheduledContentMs: number): number {
  return Math.min(Math.max(0, wallMs - gapMs), scheduledContentMs);
}

/**
 * Advance the content clock for one PCM chunk scheduled at `nowSec` with the given
 * duration. Captures the utterance t0 on the first chunk and accumulates any
 * underrun gap (audio queue drained before this chunk) so it is NOT counted as
 * played content — keeping the browser timeline equal to the avatar PTS timeline.
 */
export function scheduleChunk(state: ClockState, nowSec: number, durationSec: number): ScheduleResult {
  const prevNext = state.nextPlaybackSec;
  const startSec = Math.max(nowSec, prevNext);
  let utteranceStartSec = state.utteranceStartSec;
  let gapMs = state.gapMs;
  if (utteranceStartSec === null) {
    utteranceStartSec = startSec; // first chunk of the turn defines t0
  } else if (startSec > prevNext) {
    gapMs += (startSec - prevNext) * 1000; // queue underran → silent gap, not content
  }
  return {
    state: {
      utteranceStartSec,
      gapMs,
      scheduledContentMs: state.scheduledContentMs + durationSec * 1000,
      nextPlaybackSec: startSec + durationSec,
    },
    startSec,
  };
}

// ── Playback-context readiness (SPEC 4.11 W-A / REQ-A1) ──

/**
 * True when the playback AudioContext is not yet `running` and its resume must be
 * awaited (on the mic-click gesture) before any PCM is scheduled — so no chunk is
 * ever started against a `suspended`, frozen-`currentTime` clock (the cold-start
 * first-reply glitch the race audit could not otherwise exclude). Pure.
 */
export function contextNeedsResume(state: AudioContextState): boolean {
  return state !== 'running';
}

// ── Wire header ──

/**
 * Parse the 12-byte little-endian frame header (20 bytes for v2 "solo bocca"), or
 * return null for a bare JPEG (legacy / first-paint) so the caller can draw it
 * immediately without scheduling.
 */
export function parseFrameHeader(view: DataView): FrameHeader | null {
  if (view.byteLength < FRAME_HEADER_BYTES) return null;
  if (view.getUint16(0, true) !== FRAME_MAGIC) return null;
  const version = view.getUint8(2);
  const flags = view.getUint8(3);
  const header: FrameHeader = {
    version,
    turn: view.getUint32(4, true),
    pts: view.getUint32(8, true),
    speaking: (flags & FLAG_SPEAKING) !== 0,
    idle: (flags & FLAG_IDLE) !== 0,
  };
  if (version >= 2) {
    if (view.byteLength < FRAME_HEADER_V2_BYTES) return null;
    header.idx = view.getUint16(12, true);
    header.x = view.getUint16(14, true);
    header.y = view.getUint16(16, true);
  }
  return header;
}

// ── Video-gated audio start (DESIGN §5.2) ──

/**
 * Holds reply PCM until the avatar's first video frame for the turn is ready (or a
 * timeout), so the first sound coincides with the first correct lip frame. Inert
 * when no video viewer is connected (opens immediately → zero added latency).
 *
 * Pure state machine (no timers): the audio hook owns the setTimeout and calls
 * `release()` on timeout, and `firstFrame → release` when the frame lands.
 */
export class AudioGate<T = ArrayBuffer> {
  private _open = true;
  private _epoch: number | null = null;
  private buf: T[] = [];

  get open(): boolean {
    return this._open;
  }
  get epoch(): number | null {
    return this._epoch;
  }
  get bufferedCount(): number {
    return this.buf.length;
  }

  /**
   * Arm on `assistant_audio_start`: close the gate only when an epoch is present
   * and a viewer is connected. Returns true when the caller should start the
   * GATE_MAX_MS safety timer.
   */
  arm(epoch: number | null, connected: boolean): boolean {
    this._epoch = epoch;
    this.buf = [];
    this._open = !(epoch != null && connected);
    return !this._open;
  }

  /** A PCM chunk: returns chunks to schedule now, or [] when buffered behind the gate. */
  push(pcm: T): T[] {
    if (this._open) return [pcm];
    this.buf.push(pcm);
    return [];
  }

  /** Open the gate; returns buffered chunks to flush in order (idempotent). */
  release(): T[] {
    if (this._open) return [];
    this._open = true;
    const flushed = this.buf;
    this.buf = [];
    return flushed;
  }

  /** True when the gate is closed and `turn` matches the armed epoch. */
  matchesTurn(turn: number): boolean {
    return !this._open && turn === this._epoch;
  }

  /** Barge-in / disconnect: drop everything and forget the turn. */
  reset(): void {
    this._open = true;
    this._epoch = null;
    this.buf = [];
  }
}

// ── Dev telemetry (phase 3.3, F3) ──
//
// The pure timing math above stays DOM-free. This section is the one impure,
// dev-only corner: a bounded per-turn diagnostic surface on `window.__avsyncStats`
// populated by the audio hook (gate events) + the avatar component (draws) and
// read by the headless A/V verifier. No UI, no console output. Pure helpers
// (`newAvsyncStats`, `pushRing`) are unit-tested; the `window` glue is trivial.

export const DRAW_RING_CAP = 512; // per-turn draw-sample ring depth

export type GateReason = 'first-frame' | 'timeout' | null;

// ── ?avdebug=1 switch (phase 3.8, W6) — read once; only gates console output ──
let _avDebug: boolean | null = null;
/** True when the page was opened with `?avdebug=1`. Counters are always collected;
 *  this flag only gates the (chatty) console.debug diagnostics. */
export function avDebugEnabled(): boolean {
  if (_avDebug !== null) return _avDebug;
  if (typeof window === 'undefined') {
    _avDebug = false;
  } else {
    _avDebug = new URLSearchParams(window.location.search).get('avdebug') === '1';
  }
  return _avDebug;
}

/** One presented-frame sample: the drawn PTS against the played-audio clock. */
export interface DrawSample {
  pts: number;
  clock: number;
}

/** Per-turn A/V-sync diagnostics (reset on each assistant_audio_start). */
export interface AvsyncStats {
  turn: number | null;
  gate: {
    armTs: number | null;
    firstPcmTs: number | null;
    openTs: number | null;
    reason: GateReason;
  };
  draws: DrawSample[];
  lastContentMs: number;
  /** Cross-turn counters (W6) — carried across per-turn resets, never zeroed. */
  persistent: AvsyncPersistent;
}

/** Push onto a bounded ring in place (drops the oldest past `cap`). Pure. */
export function pushRing<T>(arr: T[], item: T, cap: number): void {
  arr.push(item);
  if (arr.length > cap) arr.shift();
}

/** Fresh per-turn stats with all timestamps unset. Pure. */
export function newAvsyncStats(turn: number | null): AvsyncStats {
  return {
    turn,
    gate: { armTs: null, firstPcmTs: null, openTs: null, reason: null },
    draws: [],
    lastContentMs: 0,
    persistent: newAvsyncPersistent(),
  };
}

type AvsyncStatsWindow = { __avsyncStats?: AvsyncStats };

/** Current dev stats object, or undefined (SSR / not yet armed). */
export function getAvsyncStats(): AvsyncStats | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as AvsyncStatsWindow).__avsyncStats;
}

/** Reset the dev stats for a new turn and return the fresh object. Cross-turn
 *  persistent counters (W6) are carried over so they accumulate across turns. */
export function resetAvsyncStats(turn: number | null): AvsyncStats {
  const persistent = getAvsyncStats()?.persistent ?? newAvsyncPersistent();
  const stats = newAvsyncStats(turn);
  stats.persistent = persistent;
  if (typeof window !== 'undefined') {
    (window as unknown as AvsyncStatsWindow).__avsyncStats = stats;
  }
  return stats;
}

/** The persistent (cross-turn) counters, lazily initialising the stats object so
 *  frame gaps / watchdog fires recorded before the first turn are not lost. */
export function getAvsyncPersistent(): AvsyncPersistent | undefined {
  if (typeof window === 'undefined') return undefined;
  return (getAvsyncStats() ?? resetAvsyncStats(null)).persistent;
}

/** Append a presented-frame sample to the current turn's bounded draw ring. */
export function recordDraw(pts: number, clock: number): void {
  const stats = getAvsyncStats();
  if (!stats) return;
  pushRing(stats.draws, { pts, clock }, DRAW_RING_CAP);
}
